import {
    getCurrentHub,
    makeFetchTransport,
    defaultStackParser,
    BrowserClient,
    Breadcrumbs,
    GlobalHandlers,
    FunctionToString,
    HttpContext,
    TryCatch
} from '@sentry/react';

import { serviceNames } from '~setup/CartService';
import { diContainer } from '~setup/diContainer';

interface IOriginalException {
    columnNumber: number;
    fileName: string;
    lineNumber: number;
    message: string;
    stack: string;
}

const dsn = process.env.SENTRY_DSN;
const environment = window.location.hostname !== 'localhost' ? process.env.NODE_ENV : 'local';
const cartServiceSignatureInStackTrace = /cart\/bundle/;

export class SentryService {
    private client: BrowserClient;

    public constructor() {
        this.initializeSentry();
    }

    private initializeSentry() {
        this.client = new BrowserClient({
            dsn,
            environment,
            allowUrls: [cartServiceSignatureInStackTrace],
            integrations: [
                new Breadcrumbs(),
                new FunctionToString(),
                new GlobalHandlers(),
                new HttpContext(),
                new TryCatch()
            ],
            beforeSend: (event, hint) => {
                const { abortRequest } = diContainer.get(serviceNames.cartRepository);
                const isCustomEvent = event?.tags && 'custom' in event.tags;
                const isRPCEvent = event?.tags && 'rpc' in event.tags;

                if (isRPCEvent && abortRequest) {
                    return null;
                }

                if (isCustomEvent) {
                    return event;
                }

                const originException = hint?.originalException as unknown as IOriginalException;
                const stackTraceFrame = event.exception?.values?.[0].stacktrace?.frames?.pop();

                const errorFileName = originException?.fileName || stackTraceFrame?.filename || '';

                return errorFileName.match(cartServiceSignatureInStackTrace) ? event : null;
            },
            tracesSampler: (samplingContext) =>
                samplingContext?.transactionContext?.name === '/cart' ? 1 : 0.25,
            transport: makeFetchTransport,
            stackParser: defaultStackParser,
            enabled: environment !== 'local'
        });

        getCurrentHub().bindClient(this.client);
    }

    public checkSentryInit() {
        const isSentryInit = getCurrentHub().getClient();

        if (isSentryInit === null) {
            this.initializeSentry();
        }
    }
}
